import { useState } from "react";
import { Dialog, DialogDescription, DialogTitle } from "@jobenvoy/ui";
import { MarkIcon } from "@/components/icons";
import { GRAD_BUTTON, OUTLINE_BUTTON } from "@/components/site/styles";
import type { TermsStatus } from "@/lib/api/auth";
import { actionProps, scopeProps } from "@/lib/controlAnalytics";
import TermsCheck from "./TermsCheck";

/** What an unticked box says when Accept is pressed. */
export const TICK_FIRST = "Tick the box to go on.";

const LINES: Record<"MISSING" | "BEHIND" | "age", string> = {
    MISSING:
        "Job Envoy now keeps a record of each person's agreement to its terms. Read the Terms and the Privacy policy, then tick the box to go on.",
    BEHIND: "The Terms or the Privacy policy changed since you last agreed. Read them, then tick the box to go on.",
    age: "Job Envoy is for adults. Confirm you are 18 or older to go on.",
};

/**
 * The prompt a signed-in person meets when the record says they have not
 * agreed to the terms in force, or never confirmed their age. It cannot
 * be closed: the way out is Sign out. A page older than the terms in
 * force (`stale`) asks for a reload instead, so no one agrees to words
 * they were not shown.
 */
export default function TermsPrompt({
    ask,
    status,
    stale,
    busy,
    error,
    onAccept,
    onSignOut,
}: {
    ask: "terms" | "age";
    status: TermsStatus;
    stale: boolean;
    busy: boolean;
    error: string | null;
    onAccept: () => void;
    onSignOut: () => void;
}) {
    const [agreed, setAgreed] = useState(false);
    const [unticked, setUnticked] = useState(false);
    const line = ask === "age" ? LINES.age : status === "BEHIND" ? LINES.BEHIND : LINES.MISSING;

    const accept = () => {
        if (!agreed) {
            setUnticked(true);
            document.getElementById("prompt-terms")?.focus();
            return;
        }
        onAccept();
    };

    return (
        <Dialog
            {...scopeProps({ source: "TermsPrompt", section: ask })}
            open
            // Escape and the scrim do nothing: this prompt is answered, not dismissed.
            onClose={() => {}}
            className="relative rounded-[22px] border border-line p-6 shadow-dialog duration-[250ms] max-[760px]:rounded-b-none max-[760px]:pb-[max(24px,env(safe-area-inset-bottom))]"
            backdropClassName="bg-scrim backdrop-blur-[3px]"
            frameClassName="max-[760px]:!p-0"
            rowClassName="max-[760px]:items-end"
            panelStyle={{ maxWidth: 440 }}
        >
            <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3.5">
                    <span
                        className="grid size-11 shrink-0 place-items-center rounded-[13px] bg-grad text-brand-fg"
                        aria-hidden="true"
                    >
                        <MarkIcon className="size-[22px]" />
                    </span>
                    <div className="flex flex-col gap-1">
                        <DialogTitle className="m-0 font-display text-[21px] font-bold leading-tight tracking-[-0.02em] sm:text-[21px]">
                            {stale ? "The terms have changed" : "Before you go on"}
                        </DialogTitle>
                        <DialogDescription className="m-0 text-[13.5px] leading-[1.45] text-fg-muted">
                            {stale
                                ? "This page was loaded before the newest Terms and Privacy policy. Reload it to read them."
                                : line}
                        </DialogDescription>
                    </div>
                </div>
                {stale ? (
                    <button
                        {...actionProps("terms_prompt_reload_clicked", { source: "TermsPrompt" })}
                        type="button"
                        onClick={() => window.location.reload()}
                        className={`${GRAD_BUTTON} h-11 rounded-control text-[14px]`}
                    >
                        Reload
                    </button>
                ) : (
                    <>
                        <TermsCheck
                            id="prompt-terms"
                            ask={ask}
                            checked={agreed}
                            error={error ?? (unticked && !agreed ? TICK_FIRST : null)}
                            action="terms_prompt_toggled"
                            source="TermsPrompt"
                            onChange={(next) => {
                                setAgreed(next);
                                setUnticked(false);
                            }}
                        />
                        <button
                            {...actionProps("terms_prompt_accepted", {
                                source: "TermsPrompt",
                                section: ask,
                            })}
                            type="button"
                            disabled={busy}
                            onClick={accept}
                            className={`${GRAD_BUTTON} h-11 rounded-control text-[14px]`}
                        >
                            {busy ? "Saving…" : "Agree and continue"}
                        </button>
                    </>
                )}
                <button
                    {...actionProps("sign_out_clicked", { source: "TermsPrompt" })}
                    type="button"
                    onClick={onSignOut}
                    className={`${OUTLINE_BUTTON} h-10 rounded-control text-[13px]`}
                >
                    Sign out
                </button>
            </div>
        </Dialog>
    );
}
